import {useCallback, useEffect, useRef, useState} from "react";
import {Alert, Box, Tab, Tabs} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useQueryClient} from "react-query";
import {useDispatch} from "react-redux";
import {useDndMonitor} from "@dnd-kit/core";
import {Reservation, ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import ReadSidebarHalfboardItem from "./ReadSidebarHalfboardItem";
import ReadSidebarUnassignedItem from "./ReadSidebarUnassignedItem";
import ReadSidebarActiveItem from "./ReadSidebarActiveItem";
import DraggableSidebarItem from "./DraggableSidebarItem";
import ReadSidebarEmptyMessage from "./ReadSidebarEmptyMessage";
import ReadSidebarTabLabel from "./ReadSidebarTabLabel";
import InfoIcon from "@mui/icons-material/Info";
import {useSidebarScroll} from "../hooks/useSidebarScroll";
import {useReadStore} from "../hooks/useReadStore";
import {useTimeframe} from "../hooks/useTimeframe";
import ReservationDialog from "../../../reservationDialog/ReservationDialog";
import {addMinutes, formatFullDMY, isToday} from "crqlar-react-common/dist/utils/date.util";
import {useCurrentApp, useCurrentAppId} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {ReservationActions, useReservationsState} from "../../../../../store/reservations.reducer";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {useService, useServicesForSector} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {useFloorplanContext} from "../hooks/useFloorplanContext";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";

interface Props {
    onLocateReservation?: () => void;
}

const ReadSidebar: React.FC<Props> = ({onLocateReservation}) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const appId = useCurrentAppId();
    const RESERVATION_TAB = 0;
    const UNASSIGNED_TAB = 1;

    const appData = useCurrentApp()
    const sectorId = appData?.configuration?.sectorId
    const sector = useSector(sectorId)
    const defaultService = useService(sector?.defaultServiceId)

    const {restaurantDraft} = useFloorplanContext();
    const accommodationSector = useAllSectors().find(s => s.sectorType === SectorType.ACCOMMODATION);
    const allAccommodationServices = useServicesForSector(accommodationSector?.id);
    const halfboardServiceIds = (restaurantDraft?.meta?.halfboardServiceIds as string[] | undefined)
        ?? (restaurantDraft?.meta?.serviceIds as string[] | undefined)
        ?? [];
    const selectedHalfboardServices = allAccommodationServices.filter(s => halfboardServiceIds.includes(s.id!));
    const hasServiceFilter = halfboardServiceIds.length > 0;

    const tab = useReadStore(s => s.sidebarTab);
    const setTab = useCallback((v: 0 | 1 | 2) => {
        localStorage.setItem('floorplanSidebarTab', String(v));
        useReadStore.getState().patch({sidebarTab: v});
    }, []);
    const [isDragging, setIsDragging] = useState(false);
    useDndMonitor({
        onDragStart: () => setIsDragging(true),
        onDragEnd: () => setIsDragging(false),
        onDragCancel: () => setIsDragging(false),
    });

    const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
    const isCreatingRef = useRef(false);
    const {newReservation, searchQuery} = useReservationsState(appId);
    const hasSearchQuery = !!(searchQuery?.trim());

    const assignedReservations = useReadStore(s => s.assignedReservations)
    const unassignedReservations = useReadStore(s => s.unassignedReservations)
    const halfboardProfiles = useReadStore(s => s.halfboardProfiles)
    const {date: selectedDate} = useTimeframe()

    const today = isToday(selectedDate);
    const dateLabel = today ? t('today') : formatFullDMY(selectedDate);

    const {
        scrollContainerRef,
        setScrollTargetRef,
        saveScrollPosition,
        targetIndex
    } = useSidebarScroll({
        active: assignedReservations,
        activeTab: tab,
    });

    const handleTabChange = (_: unknown, v: 0 | 1 | 2) => {
        saveScrollPosition(tab);
        setTab(v);
    };

    const invalidateQueries = () => {
        queryClient.invalidateQueries({queryKey: ["floorplanAssigned"]});
        queryClient.invalidateQueries({queryKey: ["floorplanUnassigned"]});
        queryClient.invalidateQueries({queryKey: ["floorplanHalfboardProfiles"]});
    };

    const handleClickEdit = useCallback((reservation: Reservation) => {
        setSelectedReservation(reservation);
    }, []);

    const activeRoomCodesText = selectedHalfboardServices
        .map((s) => s.name)
        .filter(Boolean)
        .join(', ');

    const filterSummaryText = hasServiceFilter
        ? t('The results are filtered for {{roomCodes}}.', {roomCodes: activeRoomCodesText})
        : t('No filter set');

    useEffect(() => {
        if (!newReservation) return;

        isCreatingRef.current = true;
        // We need to set some default status when creating the res
        setSelectedReservation({
            ...newReservation,
            serviceId: defaultService?.id,
            expectedEndDate: addMinutes(new Date(), defaultService?.durationMinutes || 60),
            sectorId: sector?.id,
            status: ReservationStatus.CONFIRMED
        });
        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId,
            payload: {newReservation: undefined},
        });
    }, [newReservation, dispatch, appId]);

    return (
        <>
            <Box sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                position: "relative",
            }}>
                <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    sx={{
                        height: 60,
                        px: 0,
                        borderBottom: 1,
                        borderColor: "divider",
                        "& .MuiTab-root": {
                            minHeight: 60,
                            minWidth: 0,
                            flex: 1,
                            py: 1,
                            textTransform: "none",
                        },
                    }}
                >
                    <Tab label={<ReadSidebarTabLabel
                        count={assignedReservations.length}
                        label={t("Reservations")}/>}/>
                    <Tab label={<ReadSidebarTabLabel
                        count={unassignedReservations.length}
                        label={t("Unassigned")}/>}/>
                    <Tab
                        label={<ReadSidebarTabLabel count={halfboardProfiles.length}
                                                    label={t("Arrivals")}/>}/>
                </Tabs>

                <Box
                    ref={scrollContainerRef}
                    sx={{
                        flex: 1,
                        pt: 2,
                        minHeight: 0,
                        overflowY: isDragging ? "hidden" : "auto",
                        background: `
                            linear-gradient(#fff 30%, transparent) center top / 100% 40px,
                            radial-gradient(farthest-side at 50% 0, rgba(0,0,0,.15), transparent) center top / 100% 6px
                        `,
                        backgroundRepeat: "no-repeat",
                        backgroundAttachment: "local, scroll",
                        bgcolor: "#fff",
                        overflowX: "hidden",
                    }}
                >
                    {tab === 0 && (<>
                        <Box sx={{px: 2, mb: 2}}>
                            <Alert icon={<InfoIcon fontSize="inherit"/>}
                                   severity="info">
                                {t('Reservations shown for {{date}}.', {date: dateLabel})}
                            </Alert>
                        </Box>
                        {assignedReservations.length === 0
                            ? <ReadSidebarEmptyMessage
                                text={t("No active reservations")}/>
                            : assignedReservations.map((r, i) => (
                                <Box
                                    key={r.reservationId}
                                    ref={i === targetIndex ? setScrollTargetRef : undefined}
                                >
                                    <ReadSidebarActiveItem
                                        reservation={r}
                                        onClickEdit={handleClickEdit}
                                        onClickLocate={onLocateReservation}
                                    />
                                </Box>
                            ))
                        }
                    </>)}

                    {tab === 1 && (<>
                        <Box sx={{px: 2, mb: 2}}>
                            <Alert icon={<InfoIcon fontSize="inherit"/>}
                                   severity="info">
                                {t('All reservations for {{date}} that are not yet assigned to a table.', {date: dateLabel})}
                            </Alert>
                        </Box>
                        {unassignedReservations.length === 0
                            ? <ReadSidebarEmptyMessage
                                text={t("No unassigned reservations")}/>
                            : unassignedReservations.map((r) => (
                                <DraggableSidebarItem
                                    key={r.reservationId}
                                    id={`unassigned-${r.reservationId}`}
                                    data={{type: "unassigned", reservation: r}}
                                >
                                    <ReadSidebarUnassignedItem
                                        reservation={r}
                                        onClickEdit={handleClickEdit}
                                    />
                                </DraggableSidebarItem>
                            ))
                        }
                    </>)}

                    {tab === 2 && <>
                        <Box sx={{px: 2, mb: 2}}>
                            <Alert icon={<InfoIcon fontSize="inherit"/>}
                                   severity="info">
                                {t('All guests arriving {{date}} without a restaurant reservation.', {date: dateLabel})}
                            </Alert>
                        </Box>
                        {halfboardProfiles.length === 0
                            ? (
                                <>
                                    {hasSearchQuery && (
                                        <ReadSidebarEmptyMessage
                                            text={t('No results for "{{query}}"', {query: searchQuery!.trim()})}
                                        />
                                    )}
                                    {hasServiceFilter && (
                                        <ReadSidebarEmptyMessage text={filterSummaryText}/>
                                    )}
                                </>
                            )
                            : halfboardProfiles.map((r) => (
                                <DraggableSidebarItem
                                    key={r.accommodationReservation?.id ?? r.profileIds?.[0]}
                                    id={`halfboard-${r.accommodationReservation?.id ?? r.profileIds?.[0]}`}
                                    data={{type: "halfboard", halfboard: r}}
                                >
                                    <ReadSidebarHalfboardItem
                                        halfboard={r}
                                        onClickEdit={setSelectedReservation}
                                    />
                                </DraggableSidebarItem>
                            ))
                        }
                    </>}
                </Box>
            </Box>

            <ReservationDialog
                open={!!selectedReservation}
                onClose={() => {
                    isCreatingRef.current = false;
                    setSelectedReservation(null);
                    invalidateQueries();
                }}
                reservation={selectedReservation ?? undefined}
                onSave={(reservation) => {
                    // if a reservation is created, we need to switch to the reservation tab based on whether a table is assigned
                    if (isCreatingRef.current) {
                        isCreatingRef.current = false;
                        const tableIds = new Set(useReadStore.getState().floorplanTables.map(t => t.id));
                        const hasTable = reservation?.resourceIds?.some(id => tableIds.has(id));
                        setTab(hasTable ? RESERVATION_TAB : UNASSIGNED_TAB);
                    }
                    setSelectedReservation(null);
                    invalidateQueries();
                }}
                onChangeReservation={setSelectedReservation}
            />
        </>
    );
}

export default ReadSidebar
